// Basic
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
// Libs
import { useTranslation } from 'react-i18next';
// Store & actions
import useMerchantAccountStore from '@/app/store/modules/merchantAccount';
import useColorsStore from '@/app/store/colors';
import actionsCustomization from '@/app/modules/payludd/customization/api/actionsCustomization';
// Components
import Page from '@/common-utils/components/structure/page/Page';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import PageActionsHeader from '@/app/modules/payludd/customization/components/details/PageActionsHeader';
import Functionnalities from '@/app/modules/payludd/customization/components/details/functionnalities/Functionnalities';
import CustomizationIdSection from '@/app/modules/payludd/customization/components/details/CustomizationIdSection';
import ColorLogoSection from '@/app/modules/payludd/customization/components/details/ColorLogoSection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/common-utils/components/ui/accordion';
// Common
import RouteUrls from '../../enums/RouteUrls';
import ICONS from '@/common-utils/enums/icons';
import { extractQueriesFromUrl } from '@/common-utils/functions/restUtils';
import MERCHANT_ACCOUNT_DETAILS_TABS from '../../enums/tabs/MerchantAccountDetailsTabs';
import LANGUAGE_OPTIONS from '@/app/modules/payludd/customization/enums/LanguageOptions';
import MoneticSettings from '@/app/modules/payludd/customization/components/details/MoneticSettings';
import customizationFields from '@/app/modules/payludd/customization/fields/customizationFields';
import CUSTOMIZATION_URL_ACTIONS from '@/app/modules/payludd/customization/enums/CustomizationUrlActions';

/**
 * @param root0
 * @param root0.route
 * @returns {JSX} PayluddCustomizationDetails
 */
function PayluddCustomizationDetails({ route }) {

    const { i18n } = useTranslation();

    // URL Params
    const { contractAccessId: merchantAccountId, customizationId } = useParams();
    const location = useLocation();
    const { action } = extractQueriesFromUrl(location.search);

    // Constants
    const DEFAULT_MPF_OPTIONS = [
        { name: customizationFields.MPF_OPTIONS.AMOUNT_SCREEN_DISPLAY.key, value: null },
        { name: customizationFields.MPF_OPTIONS.DEMATERIALIZED_RECEIPT_QR_CODE.key, value: null }
    ];

    // Store
    const merchantAccountDetails = useMerchantAccountStore(s => s.merchantAccountDetails);
    const dynamicBackgroundColor = useColorsStore((state) => state.dynamicBackgroundColor);

    // State
    // State - Accordion toggles
    const [openCustomizationId, setOpenCustomizationId] = useState(true);
    const [openCustomizationColors, setOpenCustomizationColors] = useState(true);
    const [openCustomizationPayluddAppSettings, setOpenCustomizationPayluddAppSettings] = useState(true);
    const [openCustomizationMoneticSettings, setOpenCustomizationMoneticSettings] = useState(true);

    // State - Payludd customization
    const defaultLocaleCode = i18n.language; // Get default locale from i18n
    const defaultLocale = LANGUAGE_OPTIONS.find((lang) => lang.key === defaultLocaleCode);
    const [customizationUuid, setCustomizationUuid] = useState('');
    const [packageCode, setPackageCode] = useState(null);
    const [packageLabel, setPackageLabel] = useState('');
    const [locale, setLocale] = useState(defaultLocale);
    const [isValidCustomization, setIsValidCustomization] = useState(false);
    const [isDirtyCustomization, setIsDirtyCustomization] = useState(false);

    // State and Constants - Payludd color and icon
    const [color, setColor] = useState(dynamicBackgroundColor);
    const [logo, setLogo] = useState('');
    const [isDirtyColorAndIcon, setIsDirtyColorAndIcon] = useState(false);

    // State - Payludd config checked options
    const [checkedMpfOptions, setCheckedMpfOptions] = useState(!action ? DEFAULT_MPF_OPTIONS : []);
    const [isValidConfig, setIsValidConfig] = useState(false);
    const [isScreenSaver, setIsScreenSaver] = useState(false);
    const [isDirtyConfig, setIsDirtyConfig] = useState(false);

    // State - Payludd monetic settings
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [isValidMoneticSettings, setIsValidMoneticSettings] = useState(false);
    const [isDirtyMoneticSettings, setIsDirtyMoneticSettings] = useState(false);
    const [isForcingGatewayOn, setIsForcingGatewayOn] = useState(false);

    // State - Global state
    const [isLoading, setIsLoading] = useState(true);
    const [isDirty, setIsDirty] = useState(false);
    const [isEverythingValid, setIsEverythingValid] = useState(false);
    const [defaultCustomization] = useState({
        packageLabel: '',
        packageCode: null,
        locale: defaultLocale.value,
        color: dynamicBackgroundColor,
        logo: '',
        mpfOptions: DEFAULT_MPF_OPTIONS,
        beneficiaries: [],
        isForcingGatewayOn: false,
        contractAccessUuid: merchantAccountId
    });

    // API Calls
    const { data: customization, isFetching: isLoadingCustomization, refetch: refetchCustomization } = actionsCustomization.getCustomization({ customizationId }, false);


    // Hooks
    useEffect(() => {
        if (customizationId && merchantAccountId) {
            refetchCustomization();
        }
    }, [customizationId, merchantAccountId]);

    // Populate form state when customization data is loaded
    useEffect(() => {
        if (customization && !isLoadingCustomization) {
            setCustomizationUuid(customization.uuid || '');
            setPackageLabel(customization.packageLabel || '');
            setPackageCode(customization.packageCode || '');
            if (action === CUSTOMIZATION_URL_ACTIONS.DUPLICATE) {
                setBeneficiaries([]);
                customization.beneficiaries = [];
            } else {
                setBeneficiaries(customization.beneficiaries || []);
            }
            setLocale(LANGUAGE_OPTIONS.find((lang) => lang.value === customization.locale) || defaultLocale);
            setColor(customization.color || dynamicBackgroundColor);
            setLogo(customization.logo || '');
            setCheckedMpfOptions(customization.mpfOptions || []);
            setIsForcingGatewayOn(customization.mpfOptions?.some(option => option.name === customizationFields.MPF_OPTIONS.FORCING_GATEWAY.key));
            setIsDirty(false);
        }
    }, [customization, dynamicBackgroundColor, defaultLocale, isLoadingCustomization]);

    // If no customization data yet, keep color in sync with user/org color once available
    useEffect(() => {
        if (!customization && dynamicBackgroundColor) {
            setColor(dynamicBackgroundColor);
        }
    }, [customization, dynamicBackgroundColor]);

    useEffect(() => {
        setIsDirty(false);
        setIsLoading(isLoadingCustomization);
    }, [isLoadingCustomization]);

    useEffect(() => {
        if (!isLoading) {
            if (isDirtyCustomization
                || isDirtyColorAndIcon
                || isDirtyConfig
                || isDirtyMoneticSettings
            ) {
                setIsDirty(true);
            } else {
                setIsDirty(false);
            }
        }
    }, [isDirty, isDirtyCustomization, isDirtyColorAndIcon, isDirtyConfig, isDirtyMoneticSettings, isLoading]);

    useEffect(() => {
        if (!isValidConfig || !isValidCustomization || !isValidMoneticSettings) {
            setIsEverythingValid(false);
        } else {
            setIsEverythingValid(true);
        }
    }, [isValidConfig, isValidCustomization, isValidMoneticSettings]);



    return (
        <Page>
            <Page.Header
                route={route}
                i18nData={{ label: merchantAccountDetails.label, packageLabel: customization?.packageLabel }}
                prevView={MERCHANT_ACCOUNT_DETAILS_TABS.CUSTOMIZATION.name}
                ids={{ contractAccessId: merchantAccountId }}
                isLoading={isLoading}
                routeBase={RouteUrls.MERCHANT_ACCOUNTS}
            />
            <Page.Body>
                <PageActionsHeader
                    customization={!action ? defaultCustomization : customization}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    action={action}
                    merchantAccountId={merchantAccountId}
                    isEverythingValid={isEverythingValid}
                    customizationPackage={
                        !action ?
                            {
                                beneficiaries: beneficiaries.map(beneficiary => beneficiary.uuid),
                                color: color,
                                contractAccessUuid: merchantAccountId,
                                locale: locale.value,
                                logo: logo,
                                mpfOptions: checkedMpfOptions,
                                packageLabel: packageLabel
                            } : {
                                beneficiaries: beneficiaries.map(beneficiary => beneficiary.uuid),
                                color: color,
                                contractAccessUuid: customization?.contractAccessUuid,
                                createdAt: customization?.createdAt,
                                locale: locale.value,
                                logo: logo,
                                mpfOptions: checkedMpfOptions,
                                packageCode: packageCode,
                                packageLabel: packageLabel,
                                scenarios: customization?.scenarios || null,
                                uuid: customizationUuid
                            }
                    }
                    setCustomizationUuid={setCustomizationUuid}
                    refetchCustomization={refetchCustomization}
                    isDirty={isDirty}
                    setIsDirty={setIsDirty}
                    setBeneficiaries={setBeneficiaries}
                />
                <div className='max-h-[calc(100vh-136px)] overflow-y-auto'>
                    <Accordion type='multiple' defaultValue={['item-1', 'item-2', 'item-3', 'item-4']}>
                        <AccordionItem className='rounded-md' value='item-1'  >
                            <AccordionTrigger
                                onClick={() => setOpenCustomizationId(!openCustomizationId)}
                                className='opacity-[.8]'
                                open={openCustomizationId}
                            >
                                <div className='flex items-center gap-2'>
                                    <Icon icon={ICONS.POS_TERMINAL} />
                                    <Entity entity={'page.payludd.customization.accordions.details'} />
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <CustomizationIdSection
                                    customization={!action ? defaultCustomization : customization}
                                    isLoading={isLoading}
                                    action={action}
                                    isPackage={!action ? false : !!customization?.packageCode}
                                    packageLabel={packageLabel}
                                    locale={locale.value}
                                    onLabelChange={setPackageLabel}
                                    onLocaleChange={setLocale}
                                    merchantAccountLabel={merchantAccountDetails?.label || ''}
                                    setIsDirtyCustomization={setIsDirtyCustomization}
                                    setIsValidCustomization={setIsValidCustomization}
                                />
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem className='rounded-md' value='item-2' >
                            <AccordionTrigger
                                onClick={() => setOpenCustomizationColors(!openCustomizationColors)}
                                className='opacity-[.8]'
                                open={openCustomizationColors}
                            >
                                <div className='flex items-center gap-2'>
                                    <Icon icon={ICONS.CUSTOMIZATION} />
                                    <Entity entity={'page.payludd.customization.accordions.colors'} />
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <ColorLogoSection
                                    customization={!action ? defaultCustomization : customization}
                                    action={action}
                                    isPackage={!action ? false : !!customization?.packageCode}
                                    isScreenSaver={isScreenSaver}
                                    isLoading={isLoading}
                                    checkedOptions={checkedMpfOptions}
                                    color={color}
                                    logo={logo}
                                    locale={locale}
                                    onColorChange={setColor}
                                    onLogoChange={setLogo}
                                    setIsDirtyColorAndIcon={setIsDirtyColorAndIcon}
                                />
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem className='rounded-md' value='item-3'>
                            <AccordionTrigger
                                onClick={() => setOpenCustomizationPayluddAppSettings(!openCustomizationPayluddAppSettings)}
                                className='opacity-[.8]'
                                open={openCustomizationPayluddAppSettings}
                            >
                                <div className='flex items-center gap-2'>
                                    <Icon icon={ICONS.SIM_CARDS_SETTINGS} />
                                    <Entity entity={'page.payludd.customization.accordions.payludd_app_settings'} />
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <Functionnalities
                                    checkedOptions={checkedMpfOptions}
                                    setCheckedOptions={setCheckedMpfOptions}
                                    customization={!action ? defaultCustomization : customization}
                                    isLoading={isLoading}
                                    action={action}
                                    isPackage={!action ? false : !!customization?.packageCode}
                                    setIsValidConfig={setIsValidConfig}
                                    setIsScreenSaver={setIsScreenSaver}
                                    setIsDirtyConfig={setIsDirtyConfig}
                                />
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem className='rounded-md' value='item-4' >
                            <AccordionTrigger
                                onClick={() => setOpenCustomizationMoneticSettings(!openCustomizationMoneticSettings)}
                                className='opacity-[.8]'
                                open={openCustomizationMoneticSettings}
                            >
                                <div className='flex items-center gap-2'>
                                    <Icon icon={ICONS.MONETIZATION} />
                                    <Entity entity={'page.payludd.customization.accordions.monetic_settings'} />
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <MoneticSettings
                                    customization={!action ? defaultCustomization : customization}
                                    isPackage={!action ? false : !!customization?.packageCode}
                                    checkedMpfOptions={checkedMpfOptions}
                                    setCheckedMpfOptions={setCheckedMpfOptions}
                                    beneficiaries={beneficiaries}
                                    setBeneficiaries={setBeneficiaries}
                                    setIsDirtyMoneticSettings={setIsDirtyMoneticSettings}
                                    isForcingGatewayOn={isForcingGatewayOn}
                                    setIsForcingGatewayOn={setIsForcingGatewayOn}
                                    setIsValidMoneticSettings={setIsValidMoneticSettings}
                                    action={action}
                                    isLoading={isLoading}
                                />
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </Page.Body>
        </Page>
    );
}

PayluddCustomizationDetails.propTypes = {
    route: PropTypes.object.isRequired
};

export default PayluddCustomizationDetails;
